import React, { createContext, useContext, useState } from 'react';
import { createPortal } from 'react-dom';

// Lo que flota (ficha, paneles laterales, recuadros de detalle) se dibuja en el body:
// el contenedor de las páginas tiene una transformación que si no los descoloca.
export const Flotante = ({ children }) => createPortal(children, document.body);

// ---------------------------------------------------------------- tipografía y controles

// Montserrat es la letra de las piezas de SURA: va en títulos y cifras; el resto, Inter
export const display = "font-['Montserrat'] tracking-tight";

export const campo = 'h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-400/50';
export const boton = 'h-9 px-3 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 disabled:opacity-40 disabled:cursor-not-allowed';
export const botonClaro = `${boton} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`;
export const botonAzul = `${boton} bg-sura-blue text-white hover:bg-sura-blue-dark`;
export const panel = 'bg-white rounded-2xl border border-slate-200/80';

// ---------------------------------------------------------------- estados de un día

// Un color por estado, el mismo en la franja, la planilla, la ficha y las leyendas
export const ESTADOS = {
    completa: { nombre: 'A tiempo', celda: 'bg-[#1F4E8C] text-white', punto: 'bg-[#1F4E8C]', texto: 'text-[#1F4E8C]' },
    retardo: { nombre: 'Llegó tarde', celda: 'bg-amber-500 text-white', punto: 'bg-amber-500', texto: 'text-amber-700' },
    salida_temprana: { nombre: 'Salió antes', celda: 'bg-amber-200 text-amber-900', punto: 'bg-amber-200', texto: 'text-amber-700' },
    una_marca: { nombre: 'Una sola marca', celda: 'bg-white text-amber-700 ring-2 ring-inset ring-amber-400', punto: 'bg-white ring-2 ring-inset ring-amber-400', texto: 'text-amber-700' },
    ausente: { nombre: 'Faltó', celda: 'bg-rose-500 text-white', punto: 'bg-rose-500', texto: 'text-rose-600' },
    justificada: { nombre: 'Vacaciones o permiso', celda: 'bg-emerald-500 text-white', punto: 'bg-emerald-500', texto: 'text-emerald-700' },
    extra: { nombre: 'Trabajó día libre', celda: 'bg-cyan-500 text-white', punto: 'bg-cyan-500', texto: 'text-cyan-700' },
    en_curso: { nombre: 'Hoy, en curso', celda: 'bg-cyan-50 text-cyan-800 ring-1 ring-inset ring-cyan-300', punto: 'bg-cyan-100 ring-1 ring-inset ring-cyan-300', texto: 'text-cyan-700' },
    libre: { nombre: 'No laborable', celda: 'bg-slate-100 text-slate-400 rayado', punto: 'bg-slate-100 rayado', texto: 'text-slate-400' },
};

export const LEYENDA = ['completa', 'retardo', 'salida_temprana', 'una_marca', 'ausente', 'justificada', 'extra', 'libre'];

export const Leyenda = ({ conteo, estados = LEYENDA }) => (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5">
        {estados.map((e) => (
            <span key={e} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                <span className={`w-3 h-3 rounded-[4px] ${ESTADOS[e].punto}`} />
                {ESTADOS[e].nombre}
                {conteo && conteo[e] != null && <span className="tabular-nums text-slate-400">{conteo[e].toLocaleString('es-VE')}</span>}
            </span>
        ))}
    </div>
);

// ---------------------------------------------------------------- fechas y horas (siempre a. m./p. m.)

export const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
export const DIAS_CORTOS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
export const DIAS_LETRA = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

export const aFecha = (iso) => {
    const [a, m, d] = iso.slice(0, 10).split('-').map(Number);
    return new Date(a, m - 1, d);
};
export const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const diaSemana = (d) => (d.getDay() + 6) % 7;   // 0 lunes
export const sumarDias = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
export const capital = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);

export const fechaLarga = (texto) => capital(aFecha(texto).toLocaleDateString('es-VE', { weekday: 'long', day: 'numeric', month: 'long' }));
export const fechaCorta = (texto) => aFecha(texto).toLocaleDateString('es-VE', { day: 'numeric', month: 'short' });

export const horaAmPm = (hhmm) => {
    if (!hhmm) return '';
    const [h, m] = hhmm.split(':').map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'a. m.' : 'p. m.'}`;
};
// Para espacios chicos: "7:52 a" sería ambiguo; se deja la hora y el sufijo en otra línea
export const partesHora = (hhmm) => {
    if (!hhmm) return null;
    const [h, m] = hhmm.split(':').map(Number);
    return { hora: `${h % 12 || 12}:${String(m).padStart(2, '0')}`, sufijo: h < 12 ? 'a. m.' : 'p. m.' };
};
export const horaDeIso = (texto) => (texto ? horaAmPm(texto.slice(11, 16)) : '');
export const numero = (n, dec = 0) => (n == null ? '' : Number(n).toLocaleString('es-VE', { minimumFractionDigits: dec, maximumFractionDigits: dec }));
export const horas = (h) => (h == null ? '' : numero(h, 1));

export const minutosTexto = (min) => {
    if (!min) return '0 min';
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    return min % 60 ? `${h} h ${min % 60} min` : `${h} h`;
};

// ---------------------------------------------------------------- personas

const TINTES = ['bg-sky-100 text-sky-800', 'bg-indigo-100 text-indigo-800', 'bg-cyan-100 text-cyan-800', 'bg-slate-200 text-slate-700', 'bg-blue-100 text-blue-800'];

const iniciales = (nombre) => (nombre || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase();

export const Avatar = ({ persona, size = 32, className = '' }) => {
    const [falla, setFalla] = useState(false);
    const nombre = persona?.nombre || '';
    const estilo = { width: size, height: size, fontSize: Math.max(9, Math.round(size * 0.36)) };
    if (persona?.foto && !falla) {
        return (
            <img
                src={persona.foto} alt="" style={estilo} onError={() => setFalla(true)} loading="lazy"
                className={`rounded-full object-cover shrink-0 bg-slate-100 ${className}`}
            />
        );
    }
    const tinte = TINTES[[...nombre].reduce((a, c) => a + c.charCodeAt(0), 0) % TINTES.length];
    return (
        <span style={estilo} className={`rounded-full shrink-0 inline-flex items-center justify-center font-semibold ${tinte} ${className}`} aria-hidden>
            {iniciales(nombre)}
        </span>
    );
};

// La ficha de una persona se abre desde cualquier nombre del módulo
const FichaContexto = createContext(() => {});
export const FichaProveedor = FichaContexto.Provider;
export const useAbrirFicha = () => useContext(FichaContexto);

export const NombrePersona = ({ persona, className = '' }) => {
    const abrir = useAbrirFicha();
    if (!persona?.empleado_id && !persona?.id) return <span className={className}>{persona?.nombre || 'Sin nombre'}</span>;
    return (
        <button
            type="button" onClick={() => abrir(persona.empleado_id || persona.id)}
            className={`text-left hover:underline decoration-slate-300 underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 rounded ${className}`}
        >
            {persona.nombre}
        </button>
    );
};

// ---------------------------------------------------------------- otros

export const Cargando = ({ alto = 'h-48' }) => (
    <div className={`flex items-center justify-center ${alto}`} role="status" aria-label="Cargando">
        <span className="w-5 h-5 rounded-full border-2 border-slate-200 border-t-sura-blue animate-spin" />
    </div>
);

export const EnVivo = ({ texto = 'En vivo' }) => (
    <span className="inline-flex items-center gap-2 text-sm text-slate-600">
        <span className="relative flex w-2.5 h-2.5">
            <span className="absolute inset-0 rounded-full bg-cyan-400 opacity-60 animate-ping motion-reduce:animate-none" />
            <span className="relative w-2.5 h-2.5 rounded-full bg-cyan-400" />
        </span>
        {texto}
    </span>
);

// Control segmentado (para elegir una vista dentro de una pantalla)
export const Segmentado = ({ opciones, valor, onCambio, etiqueta }) => (
    <div className="inline-flex p-1 bg-slate-100 rounded-xl" role="radiogroup" aria-label={etiqueta}>
        {opciones.map((o) => (
            <button
                key={o.id} type="button" role="radio" aria-checked={valor === o.id} onClick={() => onCambio(o.id)}
                className={`px-3 h-8 rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 ${
                    valor === o.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
                }`}
            >
                {o.label}
            </button>
        ))}
    </div>
);

// Períodos rápidos para los reportes
export const periodo = (clave) => {
    const hoy = new Date();
    const lunes = sumarDias(hoy, -diaSemana(hoy));
    switch (clave) {
        case 'semana_pasada': return [iso(sumarDias(lunes, -7)), iso(sumarDias(lunes, -1))];
        case 'mes': return [iso(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), iso(hoy)];
        case 'mes_pasado': return [iso(new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1)), iso(new Date(hoy.getFullYear(), hoy.getMonth(), 0))];
        default: return [iso(lunes), iso(hoy)];
    }
};
export const PERIODOS = [
    { id: 'semana', label: 'Esta semana' },
    { id: 'semana_pasada', label: 'Semana pasada' },
    { id: 'mes', label: 'Este mes' },
    { id: 'mes_pasado', label: 'Mes pasado' },
];
